/**
 * HealingCompletion — Gentle post-session screen
 * Save Session, Repeat, Create Personal Version
 * Tone: Warm, affirming, spacious
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { RotateCcw, Home, Save } from 'lucide-react';

const HealingCompletion = ({ onSave, onRepeat, onHome, homeLabel = 'Return to Healing' }) => {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
      setSaved(true);
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center px-8"
      style={{ background: 'linear-gradient(180deg, #0a0e0a 0%, #0d120d 50%, #0a0e0a 100%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2 }}
      data-testid="healing-completion"
    >
      {/* Ambient glow */}
      <motion.div
        className="fixed inset-0 pointer-events-none"
        animate={{
          background: [
            'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(140,170,120,0.04) 0%, transparent 70%)',
            'radial-gradient(ellipse 70% 55% at 50% 45%, rgba(130,165,115,0.06) 0%, transparent 70%)',
            'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(140,170,120,0.04) 0%, transparent 70%)',
          ],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="z-10 text-center max-w-sm">
        {/* Soft completion message */}
        <motion.p
          className="text-white/20 text-xs tracking-[0.2em] uppercase mb-10"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Complete
        </motion.p>

        <motion.h2
          className="text-white/60 text-2xl mb-4 leading-relaxed"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1 }}
        >
          Take a moment to notice how you feel
        </motion.h2>

        <motion.p
          className="text-white/22 text-sm mb-16 leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          There is no rush. You are welcome to stay as long as you need.
        </motion.p>

        {/* Actions */}
        <motion.div
          className="flex flex-col gap-4 items-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
        >
          {/* Save Session */}
          <motion.button
            onClick={handleSave}
            disabled={saved || saving}
            className="w-64 flex items-center justify-center gap-2.5 py-3.5 rounded-full text-sm"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: saved ? 'rgba(140,170,120,0.2)' : 'rgba(120,150,100,0.12)',
              border: `1px solid ${saved ? 'rgba(140,170,120,0.3)' : 'rgba(140,170,120,0.15)'}`,
              color: saved ? 'rgba(180,210,160,0.7)' : 'rgba(255,255,255,0.5)',
            }}
            whileHover={!saved ? { scale: 1.02 } : {}}
            whileTap={!saved ? { scale: 0.97 } : {}}
            data-testid="healing-save-btn"
          >
            <Save size={14} />
            {saving ? 'Saving...' : saved ? 'Session Saved' : 'Save Session'}
          </motion.button>

          {/* Repeat */}
          <motion.button
            onClick={onRepeat}
            className="w-64 flex items-center justify-center gap-2.5 py-3 rounded-full text-sm text-white/35"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            data-testid="healing-repeat-btn"
          >
            <RotateCcw size={14} />
            Repeat
          </motion.button>

          {/* Return Home */}
          <motion.button
            onClick={onHome}
            className="flex items-center gap-2 text-white/20 text-xs mt-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            whileTap={{ scale: 0.97 }}
            data-testid="healing-home-btn"
          >
            <Home size={12} />
            {homeLabel}
          </motion.button>
        </motion.div>
      </div>

      {/* Disclaimer */}
      <div className="absolute bottom-6 px-8 text-center">
        <p className="text-white/10 text-[10px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          This experience supports relaxation alongside your medical care.
        </p>
      </div>
    </motion.div>
  );
};

export default HealingCompletion;
